import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Plus, Trash2, Loader2, BadgePercent } from "lucide-react";
import { Input } from "../ui/input";
import AdminDateField, { londonToday } from "./AdminDateField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0 [color-scheme:dark]";
const PKGS = [["stage1", "Stage 1"], ["stage2", "Stage 2"], ["custom", "Custom Tune"]];
const STATUS = { live: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300", scheduled: "border-amber-400/40 bg-amber-400/10 text-amber-300", ended: "border-white/15 bg-white/5 text-white/45" };
const L = ({ children }) => <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">{children}</label>;
export const fmtDay = (d) => format(parseISO(d), "d MMM yyyy");
const today = londonToday;
const blank = () => ({ name: "", packages: ["stage1", "stage2", "custom"], kind: "percent", value: "", start_date: today(), end_date: today() });

const OfferForm = ({ auth, onCreated }) => {
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setF({ ...f, packages: f.packages.includes(k) ? f.packages.filter((x) => x !== k) : [...f.packages, k] });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/offers`, { ...f, value: Number(f.value) }, auth);
      toast.success(`"${f.name}" offer saved`);
      setF(blank()); onCreated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please fill in all offer fields.");
    } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="mt-6 grid gap-4 rounded-xl border border-white/10 bg-[#0b0c0f]/60 p-5 lg:grid-cols-2" data-testid="offer-form">
      <div><L>Offer name</L><Input required maxLength={40} placeholder="e.g. Black Friday" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${field} h-11`} data-testid="offer-name" /></div>
      <div>
        <L>Applies to</L>
        <div className="flex flex-wrap gap-2">
          {PKGS.map(([k, l]) => (
            <button type="button" key={k} onClick={() => toggle(k)} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${f.packages.includes(k) ? "border-brand bg-brand/15 text-white" : "border-white/15 text-white/55 hover:text-white"}`} data-testid={`offer-pkg-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <L>Discount</L>
        <div className="flex gap-2">
          <div className="grid grid-cols-2 gap-1 rounded-lg border border-white/10 p-1">
            {[["percent", "% off"], ["price", "Sale price"]].map(([k, l]) => (
              <button type="button" key={k} onClick={() => setF({ ...f, kind: k })} className={`rounded-md px-3 py-1.5 text-sm font-semibold ${f.kind === k ? "bg-brand text-white" : "text-white/60"}`} data-testid={`offer-kind-${k}`}>{l}</button>
            ))}
          </div>
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/50">{f.kind === "percent" ? "%" : "£"}</span>
            <Input required type="number" min="1" step={f.kind === "percent" ? "1" : "0.01"} value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className={`${field} h-11 pl-7`} data-testid="offer-value" />
          </div>
        </div>
        {f.kind === "price" && <p className="mt-1.5 text-[12px] text-white/40">A sale price applies to one package only.</p>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><L>Starts</L><AdminDateField value={f.start_date} onChange={(v) => setF({ ...f, start_date: v, end_date: v > f.end_date ? v : f.end_date })} testid="offer-start" /></div>
        <div><L>Ends</L><AdminDateField value={f.end_date} min={f.start_date} onChange={(v) => setF({ ...f, end_date: v })} testid="offer-end" /></div>
      </div>
      <div className="lg:col-span-2">
        <button disabled={saving || !f.packages.length} className="btn-red flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="offer-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Add offer</button>
      </div>
    </form>
  );
};

const AdminOffers = ({ token, onChanged }) => {
  const [items, setItems] = useState([]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => axios.get(`${API}/admin/offers`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setItems(data)).catch(() => toast.error("Couldn't load offers")), [token]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged(); };
  const remove = async (o) => {
    try { await axios.delete(`${API}/admin/offers/${o.id}`, auth); toast.success(`"${o.name}" removed`); refresh(); }
    catch { toast.error("Couldn't remove offer"); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-[#111217] p-6" data-testid="offers-editor">
      <h2 className="flex items-center gap-2 font-display text-3xl font-bold uppercase text-white"><BadgePercent className="h-7 w-7 text-brand" />Offers & discounts</h2>
      <p className="mt-1 text-sm text-white/50">Offers switch on and off by themselves on the dates you pick. Customers see the old price crossed out and the offer badge, and online checkout charges the offer price.</p>
      <div className="mt-5 space-y-2" data-testid="offers-list">
        {items.length === 0 && <div className="rounded-lg border border-dashed border-white/10 p-5 text-center text-sm text-white/40">No offers yet.</div>}
        {items.map((o) => (
          <div key={o.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-[#0b0c0f] px-4 py-3" data-testid={`offer-row-${o.id}`}>
            <span className={`rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase ${STATUS[o.status]}`}>{o.status}</span>
            <span className="font-semibold text-white">{o.name}</span>
            <span className="text-sm text-brand">{o.kind === "percent" ? `${o.value}% off` : `£${o.value}`}</span>
            <span className="text-sm text-white/55">{o.packages.map((k) => PKGS.find(([x]) => x === k)?.[1]).join(", ")}</span>
            <span className="text-sm text-white/45">{fmtDay(o.start_date)} – {fmtDay(o.end_date)}</span>
            <button onClick={() => remove(o)} className="ml-auto flex items-center gap-1 text-sm text-white/50 hover:text-brand" data-testid={`offer-delete-${o.id}`}><Trash2 className="h-4 w-4" />Remove</button>
          </div>
        ))}
      </div>
      <OfferForm auth={auth} onCreated={refresh} />
    </div>
  );
};

export default AdminOffers;
